import {
  Outlet,
  Link,
  Navigate,
  createRootRouteWithContext,
  useRouterState,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { CircleUserRound, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSession, useUserProfile, signOut } from "../lib/auth.js";
import { useOffline } from "../lib/offline-context.js";
import { decideRoleGate } from "../lib/role-gate.js";
import { AccountDrawer } from "../components/AccountDrawer.js";
import { Brand } from "../components/Brand.js";
import { LanguageSwitcher } from "../components/LanguageSwitcher.js";
import { SyncBanner } from "../components/SyncBanner.js";
import { SyncToasts } from "../components/SyncToasts.js";
import { SyncErrorToasts } from "../components/SyncErrorToasts.js";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootLayout,
});

/**
 * Garde de rôle (mirror admin-front __root.tsx, commit c0a8ebe) : Poseurs est
 * réservé au rôle `poseur`. login.tsx vérifie déjà le rôle à la connexion
 * interactive, mais ne couvre pas une session restaurée depuis le
 * localStorage au boot de l'app (PWA relancée, onglet rechargé) — ce garde
 * revérifie donc à chaque session présente. Décision pure dans role-gate.ts
 * (testée indépendamment) : offline-tolérant, ne bloque jamais un poseur
 * légitime faute de réseau.
 */
function RootLayout() {
  const { t } = useTranslation();
  const session = useSession();
  const profile = useUserProfile();
  const { connectionState } = useOffline();
  const [accountOpen, setAccountOpen] = useState(false);

  // `href` = pathname + search + hash : l'URL complète à rejouer après
  // reconnexion, paramètres de tracking des mails compris.
  const href = useRouterState({ select: (s) => s.location.href });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Garde de session, en amont du garde de rôle. Sans elle, un lien de PV
  // ouvert avec une session morte laissait `/pv/$taskId` sur « Chargement… »
  // indéfiniment (le service offline n'est monté que pour une session
  // authentifiée) — et l'URL demandée était perdue.
  const onLoginPath = pathname.replace(/\/$/, "") === "/login";
  const needsLogin = session.status === "anon" && !onLoginPath;

  const gate =
    session.status === "authed"
      ? decideRoleGate({
          profileStatus: profile.status,
          role: profile.status === "ready" ? profile.profile.role : null,
          connectionState,
        })
      : null;

  return (
    <div className="min-h-screen bg-background text-foreground font-sans flex flex-col">
      <header className="border-b px-4 py-3 flex items-center justify-between gap-3">
        <Link to="/" aria-label={`${t("common.brand")} — accueil`}>
          <Brand />
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          {session.status === "authed" && (
            <>
              <button
                type="button"
                onClick={() => setAccountOpen(true)}
                className="flex items-center gap-1.5 text-sm text-muted-foreground"
              >
                <CircleUserRound className="size-5" aria-hidden />
                {t("dashboard.profileLink")}
              </button>
              <button
                type="button"
                onClick={() => void signOut()}
                className="flex items-center gap-1.5 text-sm text-muted-foreground"
              >
                <LogOut className="size-4" aria-hidden />
                {t("auth.logout")}
              </button>
            </>
          )}
        </div>
      </header>
      {session.status === "authed" && (
        <AccountDrawer
          open={accountOpen}
          email={session.session.user.email ?? ""}
          onClose={() => setAccountOpen(false)}
        />
      )}
      {gate === "allowed" && <SyncBanner />}
      <main className="p-4 flex-1 max-w-2xl mx-auto w-full flex flex-col min-h-0">
        {needsLogin ? (
          <Navigate to="/login" search={{ redirect: href }} replace />
        ) : gate === "denied" ? (
          <AccessDenied />
        ) : gate === "checking" ||
          (session.status === "loading" && !onLoginPath) ? (
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        ) : (
          <Outlet />
        )}
      </main>
      {gate === "allowed" && (
        <>
          <SyncToasts />
          <SyncErrorToasts />
        </>
      )}
    </div>
  );
}

function AccessDenied() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="max-w-sm rounded-lg border bg-card p-6 text-center shadow-sm">
        <h1 className="text-lg font-semibold">{t("auth.accessDenied.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("auth.accessDenied.body")}</p>
        <button
          onClick={() => void signOut()}
          className="mt-4 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {t("auth.logout")}
        </button>
      </div>
    </div>
  );
}
